import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { partnerRulesApi, retailerApi } from '../../services/api';
import {
  COLORS,
  EffectBar,
  EffectLegend,
  RuleFields,
  fmt,
  fromRule,
  pct,
  projectUnknown,
  toPayload,
  total,
  windowLabel,
} from './shared';

// One partner rule (Phase 4a, plan §3.2): what can be changed (pencil) and what is fixed (lock),
// next to what the rule did on both sides. Changing who/where makes a new version (Q2), so the
// page moves to the new id after a save.

const SHOW = 'show';
const EXCLUDE = 'exclude';

function SettingRow({ label, value, action, help, locked }) {
  return (
    <Box sx={{ py: 2, borderBottom: '1px solid #ededed' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {locked && <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-label="Fixed for every rule" />}
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
        <Box>{value}</Box>
        {action}
      </Stack>
      {help && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {help}
        </Typography>
      )}
    </Box>
  );
}

function SideEffect({ title, buckets, draft, scoped, shownLabel }) {
  const shown = draft || buckets;
  const t = total(shown);
  const delta = draft ? (draft.shown + draft.shown_unknown) - (buckets.shown + buckets.shown_unknown) : 0;
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {fmt(t)} times
        </Typography>
      </Stack>
      <EffectBar buckets={shown} label={title} />
      <EffectLegend buckets={shown} scoped={scoped} />
      {draft && delta !== 0 && (
        <Typography variant="body2" sx={{ color: '#8a3c12', bgcolor: '#fdf3ec', borderRadius: 1, px: 1.25, py: 0.75 }}>
          {delta > 0 ? '+' : '−'}
          {fmt(Math.abs(delta))} times {shownLabel} {delta > 0 ? 'would be shown' : 'would be hidden'}.
        </Typography>
      )}
    </Stack>
  );
}

export default function PartnerRuleDetail() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [rule, setRule] = useState(null);
  const [error, setError] = useState(null);
  const [draftUnknown, setDraftUnknown] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [stopReason, setStopReason] = useState('');
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeValue, setChangeValue] = useState(null);
  const [retailers, setRetailers] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRule(await partnerRulesApi.get(ruleId, days));
      setDraftUnknown(null);
    } catch (e) {
      setError(e.message);
    }
  }, [ruleId, days]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    retailerApi.list().then((d) => setRetailers(d.retailers || [])).catch(() => setRetailers([]));
  }, []);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const out = await fn();
      if (out?.id && out.id !== ruleId) navigate(`/partner-rules/${out.id}`);
      else await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  if (error && !rule) return <Alert severity="error">{error}</Alert>;
  if (!rule) return <CircularProgress />;

  const owner = rule.owner_name;
  const scoped = rule.scoped;
  const current = rule.unknown_excluded ? EXCLUDE : SHOW;
  const changing = draftUnknown !== null && draftUnknown !== current;
  const draft = (b) => (changing ? projectUnknown(b, draftUnknown === EXCLUDE) : null);
  const partnerName = rule.partner_name;
  const where = rule.where;
  const changePayload = changeValue ? toPayload(changeValue).payload : null;

  return (
    <Stack spacing={3}>
      <Box>
        <Button size="small" onClick={() => navigate('/partner-rules')}>
          ← Partner rules
        </Button>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4">{owner}</Typography>
            <Typography color="text.secondary">{rule.reason_note || 'No note'}</Typography>
          </Box>
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined">
        <Box sx={{ p: 3, borderBottom: '1px solid #ededed' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={rule.rule_type === 'catchment_exclusion' ? 'All partners' : 'One partner'} />
            <Chip
              size="small"
              color={rule.live ? 'success' : 'default'}
              variant="outlined"
              label={
                rule.live
                  ? `On since ${new Date(rule.effective_from).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`
                  : `Stopped ${new Date(rule.effective_until).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`
              }
            />
          </Stack>
          <Typography variant="h5" sx={{ mt: 1 }}>
            {rule.sentence}
          </Typography>
          <Typography color="text.secondary">
            {rule.rule_type === 'catchment_exclusion'
              ? `Shoppers this rule covers see no partners on ${owner}'s pages, and do not see ${owner} on partner pages.`
              : `Shoppers this rule covers do not see ${partnerName} on ${owner}'s pages, and do not see ${owner} on ${partnerName}'s pages.`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 380px', minWidth: 0, px: 3, pb: 2, borderRight: { md: '1px solid #ededed' } }}>
            <SettingRow
              label="Rule"
              value={
                <FormControlLabel
                  control={
                    <Switch
                      checked={rule.live}
                      disabled={busy}
                      onChange={(e) =>
                        e.target.checked ? act(() => partnerRulesApi.restart(rule.id)) : setStopOpen(true)
                      }
                    />
                  }
                  label={rule.live ? 'On' : 'Off'}
                />
              }
              help={
                rule.live
                  ? 'Turning it off stops it at once, on both sides.'
                  : 'Turning it on starts a new version with the same settings.'
              }
            />
            <SettingRow
              label="Who and where"
              value={
                <>
                  <Typography>{rule.rule_type === 'catchment_exclusion' ? 'All partners' : partnerName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {where}
                  </Typography>
                </>
              }
              action={
                rule.live && (
                  <Button
                    size="small"
                    onClick={() => {
                      setChangeValue(fromRule(rule));
                      setChangeOpen(true);
                    }}
                  >
                    Change
                  </Button>
                )
              }
              help="A change takes effect at once. Its effect shows here after a day."
            />
            {scoped && (
              <SettingRow
                label="Shoppers we can't locate"
                value={
                  <RadioGroup value={draftUnknown ?? current} onChange={(e) => setDraftUnknown(e.target.value)}>
                    <FormControlLabel value={SHOW} control={<Radio />} label="Don't exclude them" disabled={!rule.live} />
                    <FormControlLabel value={EXCLUDE} control={<Radio />} label="Exclude them too" disabled={!rule.live} />
                  </RadioGroup>
                }
                help="Phone networks and VPNs often hide where a shopper is. This applies on both sides, like the rest of the rule."
              />
            )}
            <SettingRow
              locked
              label="Works both ways"
              value={<Typography variant="body2">Always. No partner can switch it off for its own pages.</Typography>}
            />
            <SettingRow
              locked
              label="Who can change it"
              value={
                <Typography variant="body2">
                  {owner}'s admin users ({rule.owner_admin_users}) in their dashboard, and HandOff here.
                </Typography>
              }
            />
          </Box>

          <Box sx={{ flex: '999 1 520px', minWidth: 0, p: 3 }}>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h6">Effect {windowLabel(rule)}</Typography>
                <ToggleButtonGroup size="small" exclusive value={days} onChange={(e, d) => d && setDays(d)}>
                  {[7, 30, 90].map((d) => (
                    <ToggleButton key={d} value={d}>
                      {d} days
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Times a shopper looked at a sold-out size that the rule was checked for.
              </Typography>
              <SideEffect
                title={`On ${owner}'s pages`}
                buckets={rule.effect.owner}
                draft={draft(rule.effect.owner)}
                scoped={scoped}
                shownLabel="a partner"
              />
              <SideEffect
                title={rule.rule_type === 'catchment_exclusion' ? 'On partner pages' : `On ${partnerName}'s pages`}
                buckets={rule.effect.partners}
                draft={draft(rule.effect.partners)}
                scoped={scoped}
                shownLabel={owner}
              />
              {changing && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, border: `1px solid ${COLORS.hidden_unknown}`, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Saving changes this rule on both sides at once. {owner} sees the change in their dashboard.
                  </Typography>
                  <Button onClick={() => setDraftUnknown(null)}>Cancel</Button>
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={() => act(() => partnerRulesApi.change(rule.id, { unknown_excluded: draftUnknown === EXCLUDE }))}
                  >
                    Save change
                  </Button>
                </Stack>
              )}
              {!!rule.other_live_rules.length && (
                <Alert severity="info">
                  {owner} has {rule.other_live_rules.length} other rule{rule.other_live_rules.length > 1 ? 's' : ''} on (
                  {rule.other_live_rules.map((r) => r.sentence).join('; ')}). When two rules cover the same shopper, the
                  hidden count goes to one of them, so these numbers can be lower than this rule's reach.
                </Alert>
              )}
            </Stack>
          </Box>
        </Box>
      </Paper>

      {!!rule.by_partner.length && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Per partner, {windowLabel(rule)}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">{owner} shown on their pages</TableCell>
                  <TableCell align="right">Hidden · in the area</TableCell>
                  <TableCell align="right">Hidden · can't locate</TableCell>
                  <TableCell align="right">Share hidden</TableCell>
                  <TableCell align="right">Partner hidden on {owner}'s pages</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rule.by_partner.slice(0, 8).map((p) => {
                  const t = p.shown + p.shown_unknown + p.hidden_area + p.hidden_unknown;
                  return (
                    <TableRow key={p.retailer_id}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.shown + p.shown_unknown)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.hidden_area)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.hidden_unknown)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{pct(p.hidden_area + p.hidden_unknown, t)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(p.hidden_on_owner_pages)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          {rule.by_partner.length > 8 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              And {rule.by_partner.length - 8} smaller partners.
            </Typography>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          History
        </Typography>
        <Stack spacing={1}>
          {rule.history.map((v) => (
            <Stack key={v.id} direction="row" spacing={2} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', minWidth: 170 }}>
                {new Date(v.effective_from).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
              </Typography>
              <Typography variant="body2">
                {v.supersedes_id ? 'Changed to' : 'Turned on'}: {v.sentence}
                {v.scoped ? `, shoppers we can't locate ${v.unknown_excluded ? 'excluded too' : 'not excluded'}` : ''}.
                By {v.created_by}.
                {v.effective_until && !v.live && (
                  <>
                    {' '}
                    Ended {new Date(v.effective_until).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                    {v.deactivated_by ? ` by ${v.deactivated_by}` : ''}
                    {v.stop_reason ? ` — ${v.stop_reason}` : ''}.
                  </>
                )}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Dialog open={stopOpen} onClose={() => setStopOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Stop this rule?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {rule.sentence}. It stops at once, on both sides. You can turn it on again later.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Reason (for HandOff)"
            value={stopReason}
            onChange={(e) => setStopReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!stopReason.trim() || busy}
            onClick={() => {
              setStopOpen(false);
              act(() => partnerRulesApi.stop(rule.id, stopReason.trim()));
            }}
          >
            Stop rule
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={changeOpen} onClose={() => setChangeOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Change {owner}'s rule</DialogTitle>
        <DialogContent>
          {changeValue && (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <RuleFields
                ownerId={rule.owner_retailer_id}
                ownerName={owner}
                value={changeValue}
                onChange={setChangeValue}
                retailers={retailers}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!changePayload || busy}
            onClick={() => {
              setChangeOpen(false);
              act(() => partnerRulesApi.change(rule.id, changePayload));
            }}
          >
            Save change
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
